/* BEATSEEK — community ID suggestions. These NEVER relabel the engine's result:
   they sit quietly underneath it, and anyone holding the file gets funnelled to
   the Community Edit Index so the engine learns it from a real fingerprint. */
import React, { useEffect, useState } from 'react';
import { ThumbsUp, ThumbsDown, Dna, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const VERSION_LABEL = {
  original: 'original mix', remix: 'remix', edit: 'edit', bootleg: 'bootleg', vip: 'VIP', rework: 'rework',
  flip: 'flip', mashup: 'mashup', extended: 'extended mix', radio: 'radio edit', club: 'club mix', dub: 'dub',
};

export const CommunitySuggestions = ({ track, userId, onFingerprint }) => {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const skip = !track || !track.title || ['not_found', 'error', 'rate_limit'].includes(track.id) || track.outsideScope;

  useEffect(() => {
    if (skip) { setItems([]); return; }
    const q = new URLSearchParams({ artist: track.artist || '', title: track.title || '' });
    if (userId) q.set('user_id', userId);
    fetch(`${API}/api/track-id/suggestions?${q}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setItems((d && d.suggestions) || []))
      .catch(() => setItems([]));
    // eslint-disable-next-line
  }, [track?.title, track?.artist, userId]);

  if (skip) return null;

  const vote = async (s, dir) => {
    if (!userId) return toast.error('Log in to vote on community suggestions');
    setBusyId(s.id);
    try {
      const next = s.my_vote === dir ? '' : dir;
      const r = await fetch(`${API}/api/track-id/suggestions/${s.id}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('beatseek_token') || ''}` },
        body: JSON.stringify({ vote: next }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Could not save your vote');
      setItems(prev => prev.map(it => (it.id === s.id
        ? { ...it, votes_up: d.votes_up, votes_down: d.votes_down, my_vote: d.my_vote, backed: it.curated || d.backed }
        : it)));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const nameOf = (s) => {
    const title = s.suggested_title || track.title;
    const who = s.suggested_artist ? ` — ${s.suggested_artist}` : '';
    const vt = VERSION_LABEL[s.version_type] ? ` (${VERSION_LABEL[s.version_type]})` : '';
    return `${title}${who}${vt}`;
  };

  return (
    <div className="id-suggestions" data-testid="id-community-suggestions">
      {items.map((s) => (
        <div key={s.id} className={`id-sugg ${s.backed ? 'backed' : ''}`} data-testid={`id-suggestion-${s.id}`}>
          <span className="id-sugg-lead">
            {s.curated && <img src="/beatseek-b-trans-1024.png" alt="BEATSEEK verified" className="id-sugg-b" data-testid="id-suggestion-b-icon" />}
            {s.curated ? 'BEATSEEK says this may be:' : 'Community says this may be:'}
          </span>
          <span className="id-sugg-name">{nameOf(s)}</span>
          {s.curated && <span className="id-sugg-admin" data-testid="id-suggestion-admin-badge">ADMIN</span>}
          {s.link && (
            <a href={s.link} target="_blank" rel="noopener noreferrer" className="id-sugg-proof" data-testid={`id-suggestion-proof-${s.id}`}>proof ↗</a>
          )}
          <span className="id-sugg-votes">
            <button
              type="button"
              className={`id-sugg-vote ${s.my_vote === 'up' ? 'on' : ''}`}
              disabled={busyId === s.id}
              onClick={() => vote(s, 'up')}
              title="That's it"
              data-testid={`id-suggestion-up-${s.id}`}
            >
              <ThumbsUp size={11} /> {s.votes_up || 0}
            </button>
            <button
              type="button"
              className={`id-sugg-vote ${s.my_vote === 'down' ? 'on down' : ''}`}
              disabled={busyId === s.id}
              onClick={() => vote(s, 'down')}
              title="Nope"
              data-testid={`id-suggestion-down-${s.id}`}
            >
              <ThumbsDown size={11} /> {s.votes_down || 0}
            </button>
          </span>
          {!s.backed && !s.curated && <span className="id-sugg-pending">needs votes</span>}
        </div>
      ))}

      <button
        type="button"
        className="id-sugg-funnel"
        data-testid="id-fingerprint-funnel"
        onClick={() => (onFingerprint ? onFingerprint(track) : null)}
      >
        <Dna size={12} /> Got the file? Fingerprint it in the Edit Index <ArrowRight size={11} />
      </button>
      <span className="id-sugg-note">Suggestions never rename the engine's ID — only a real fingerprint does.</span>
    </div>
  );
};

export default CommunitySuggestions;
